import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const fmtDate = (d) => {
  if (!d) return '';
  // Handle both date-only strings ('2026-05-14') and timestamps ('2026-05-14T...').
  // For date-only values, parse parts manually so we get a LOCAL date — `new Date('2026-05-14')`
  // parses as UTC midnight, which renders as the previous day in west-of-UTC timezones.
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [year, month, day] = s.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtHour = (h) => `${h % 12 || 12}${h >= 12 ? 'p' : 'a'}`;
const fmtUptime = (s) => {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const StatCard = ({ label, value, hint }) => (
  <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '0.9rem 1.1rem' }}>
    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#9a9a8a' }}>{label}</div>
    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 500, color: '#0d2b1a', lineHeight: 1.1, marginTop: 4 }}>{value}</div>
    {hint && <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 3 }}>{hint}</div>}
  </div>
);

// Simple inline bar chart (no external chart lib needed here)
const BarChart = ({ data, labelKey, valueKey, formatLabel }) => {
  if (!data || data.length === 0) return <div style={{ fontSize: 12, color: '#9a9a8a' }}>No data yet.</div>;
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 100, paddingTop: 4 }}>
      {data.map((d, i) => {
        const pct = (d[valueKey] / max) * 100;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <div style={{ fontSize: 9, color: '#5a5a4a' }}>{d[valueKey] || ''}</div>
            <div title={`${d[valueKey]}`} style={{
              width: '100%',
              height: `${pct}%`,
              minHeight: d[valueKey] > 0 ? 2 : 0,
              background: '#2e7d4f',
              borderRadius: '3px 3px 0 0',
            }} />
            <div style={{ fontSize: 9, color: '#9a9a8a', whiteSpace: 'nowrap' }}>
              {formatLabel ? formatLabel(d[labelKey]) : d[labelKey]}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── User row with inline edit + actions ─────────────────────────
const UserRow = ({ u, onChanged, currentUserId }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: u.first_name || '',
    last_name:  u.last_name  || '',
    email:      u.email      || '',
    phone:      u.phone      || '',
    is_admin:   !!u.is_admin,
    share_phone:!!u.share_phone,
  });
  const [saving, setSaving] = useState(false);
  const isSelf = u.id === currentUserId;
  const isDeactivated = !!u.deleted_at;

  const save = async () => {
    setSaving(true);
    try {
      await api.updateAdminUser(u.id, form);
      setEditing(false);
      onChanged();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!window.confirm(`Deactivate ${u.first_name} ${u.last_name}? They won't be able to log in.`)) return;
    try { await api.deactivateUser(u.id); onChanged(); }
    catch (err) { alert(err.message); }
  };

  const reactivate = async () => {
    try { await api.reactivateUser(u.id); onChanged(); }
    catch (err) { alert(err.message); }
  };

  const passwordReset = async () => {
    try {
      const { resetUrl, expiresIn, userEmail } = await api.generatePasswordReset(u.id);
      try {
        await navigator.clipboard.writeText(resetUrl);
        alert(`Password reset link copied to clipboard.\n\nFor: ${userEmail}\nExpires in: ${expiresIn}\n\nSend this link to the user.`);
      } catch {
        // Clipboard can fail in some browsers — fall back to prompt
        window.prompt(`Password reset link for ${userEmail} (expires ${expiresIn}):`, resetUrl);
      }
    } catch (err) { alert(err.message); }
  };

  const cellStyle = { padding: '6px 6px', verticalAlign: 'top' };
  const inputStyle = { width: '100%', fontSize: 12, padding: '3px 5px', border: '1px solid #d8d4c4', borderRadius: 4, fontFamily: 'inherit' };

  return (
    <tr style={{ borderBottom: '0.5px solid #f0ede4', background: isDeactivated ? '#faf6ed' : 'transparent', opacity: isDeactivated ? 0.7 : 1 }}>
      <td style={cellStyle}>
        {editing ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} style={inputStyle} placeholder="First" />
            <input value={form.last_name}  onChange={e => setForm(f => ({...f, last_name:  e.target.value}))} style={inputStyle} placeholder="Last" />
          </div>
        ) : (
          <div style={{ color: '#0d2b1a', fontWeight: 500 }}>
            {u.first_name} {u.last_name}
            {u.is_admin && <span style={{ marginLeft: 6, fontSize: 9, background: '#0d2b1a', color: '#7ec99a', padding: '1px 5px', borderRadius: 8, textTransform: 'uppercase' }}>admin</span>}
            {isDeactivated && <span style={{ marginLeft: 6, fontSize: 9, background: '#a13', color: '#fff', padding: '1px 5px', borderRadius: 8, textTransform: 'uppercase' }}>deactivated</span>}
          </div>
        )}
      </td>
      <td style={cellStyle}>
        {editing
          ? <input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} style={inputStyle} />
          : <span style={{ color: '#5a5a4a', fontSize: 12 }}>{u.email}</span>}
      </td>
      <td style={cellStyle}>
        {editing
          ? <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} style={inputStyle} placeholder="Phone" />
          : <span style={{ color: '#5a5a4a', fontSize: 12 }}>{u.phone || '—'}</span>}
      </td>
      <td style={cellStyle}>
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11 }}>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_admin} onChange={e => setForm(f => ({...f, is_admin: e.target.checked}))} style={{ width: 'auto' }} />
              admin
            </label>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="checkbox" checked={form.share_phone} onChange={e => setForm(f => ({...f, share_phone: e.target.checked}))} style={{ width: 'auto' }} />
              share phone
            </label>
          </div>
        ) : (
          <span style={{ fontSize: 11, color: '#7a7a6a' }}>
            {u.tee_times_hosted}h · {u.tee_times_joined}j
          </span>
        )}
      </td>
      <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {editing ? (
          <>
            <button type="button" className="btn btn-sm" onClick={save} disabled={saving} style={{ marginRight: 4 }}>{saving ? '…' : 'Save'}</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Edit</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={passwordReset} title="Generate reset link">Reset PW</button>
            {isDeactivated
              ? <button type="button" className="btn btn-sm" onClick={reactivate}>Reactivate</button>
              : !isSelf && <button type="button" className="btn btn-outline btn-sm" onClick={deactivate} style={{ color: '#a13', borderColor: '#f4d4c4' }}>Deactivate</button>}
          </div>
        )}
      </td>
    </tr>
  );
};

// ─── Course row with inline edit + merge selection ───────────────
const CourseRow = ({ c, onChanged, isSelected, onSelectChange }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: c.name || '', url: c.url || '', phone: c.phone || '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateAdminCourse(c.id, form);
      setEditing(false);
      onChanged();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${c.name}"? This is only allowed because no tee times reference it.`)) return;
    try { await api.deleteAdminCourse(c.id); onChanged(); }
    catch (err) { alert(err.message); }
  };

  const cellStyle = { padding: '6px 6px', verticalAlign: 'top' };
  const inputStyle = { width: '100%', fontSize: 12, padding: '3px 5px', border: '1px solid #d8d4c4', borderRadius: 4, fontFamily: 'inherit' };

  return (
    <tr style={{ borderBottom: '0.5px solid #f0ede4', background: isSelected ? '#f0ede4' : 'transparent' }}>
      <td style={{ ...cellStyle, width: 28 }}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelectChange(c.id, e.target.checked)}
          title="Select for merge"
        />
      </td>
      <td style={cellStyle}>
        {editing
          ? <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={inputStyle} />
          : <span style={{ color: '#0d2b1a', fontWeight: 500 }}>{c.name}</span>}
      </td>
      <td style={cellStyle}>
        {editing
          ? <input value={form.url} onChange={e => setForm(f => ({...f, url: e.target.value}))} style={inputStyle} placeholder="https://…" />
          : (c.url ? <a href={c.url} target="_blank" rel="noreferrer" style={{ color: '#5a5a4a', fontSize: 11, wordBreak: 'break-all' }}>{c.url}</a> : <span style={{ color: '#9a9a8a', fontSize: 11 }}>—</span>)}
      </td>
      <td style={cellStyle}>
        {editing
          ? <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} style={inputStyle} placeholder="(509) 555-1234" />
          : <span style={{ color: '#5a5a4a', fontSize: 12 }}>{c.phone || '—'}</span>}
      </td>
      <td style={{ ...cellStyle, textAlign: 'right' }}>
        <span style={{ fontSize: 11, color: '#7a7a6a' }}>{c.tee_time_count}</span>
      </td>
      <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {editing ? (
          <>
            <button type="button" className="btn btn-sm" onClick={save} disabled={saving} style={{ marginRight: 4 }}>{saving ? '…' : 'Save'}</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Edit</button>
            {c.tee_time_count === 0 && (
              <button type="button" className="btn btn-outline btn-sm" onClick={del} style={{ color: '#a13', borderColor: '#f4d4c4' }}>Delete</button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

// ─── Live tee time row (admin edit) ─────────────────────────
const LiveTeeTimeRow = ({ tt, courses, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // Initial form values mirror the row. Time comes back as 'HH:MM:SS', clip to 'HH:MM' for the input.
  const initialForm = () => ({
    courseId:    tt.course_id || '',
    teeDate:     tt.tee_date ? String(tt.tee_date).slice(0, 10) : '',
    teeTime:     tt.tee_time ? String(tt.tee_time).slice(0, 5)  : '',
    totalSpots:  tt.total_spots || 4,
    holes:       tt.holes || 18,
    courseUrl:   tt.course_url   || '',
    coursePhone: tt.course_phone || '',
    cancellationUrl: tt.cancellation_url || '',
  });
  const [form, setForm] = useState(initialForm);

  const beginEdit = () => { setForm(initialForm()); setEditing(true); };

  const save = async () => {
    setSaving(true);
    try {
      // Send only the fields that actually changed, so the audit log diff is meaningful
      const payload = {};
      if (form.courseId        !== (tt.course_id || ''))                        payload.courseId        = form.courseId || null;
      if (form.teeDate         !== String(tt.tee_date || '').slice(0, 10))      payload.teeDate         = form.teeDate;
      if (form.teeTime         !== String(tt.tee_time || '').slice(0, 5))       payload.teeTime         = form.teeTime;
      if (parseInt(form.totalSpots) !== tt.total_spots)                          payload.totalSpots      = parseInt(form.totalSpots);
      if (parseInt(form.holes)      !== tt.holes)                                payload.holes           = parseInt(form.holes);
      if (form.courseUrl       !== (tt.course_url       || ''))                  payload.courseUrl       = form.courseUrl;
      if (form.coursePhone     !== (tt.course_phone     || ''))                  payload.coursePhone     = form.coursePhone;
      if (form.cancellationUrl !== (tt.cancellation_url || ''))                  payload.cancellationUrl = form.cancellationUrl;
      if (Object.keys(payload).length === 0) { setEditing(false); setSaving(false); return; }
      await api.updateAdminTeeTime(tt.id, payload);
      setEditing(false);
      onChanged();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const cellStyle  = { padding: '6px 6px', verticalAlign: 'top' };
  const inputStyle = { fontSize: 12, padding: '3px 5px', border: '1px solid #d8d4c4', borderRadius: 4, fontFamily: 'inherit' };
  const filled = (tt.player_count || 0) + (tt.guest_count || 0);
  const invitedGuests = tt.invited_guest_count || 0;

  if (!editing) {
    return (
      <tr style={{ borderBottom: '0.5px solid #f0ede4' }}>
        <td style={{ ...cellStyle, color: '#0d2b1a', fontWeight: 500 }}>{tt.course_name}</td>
        <td style={{ ...cellStyle, color: '#5a5a4a' }}>
          {tt.tee_date ? fmtDate(tt.tee_date) : ''} {tt.tee_time ? String(tt.tee_time).slice(0, 5) : ''}
        </td>
        <td style={{ ...cellStyle, color: '#5a5a4a' }}>
          {tt.host_name}
          <br/><span style={{ fontSize: 10, color: '#9a9a8a' }}>{tt.host_email}</span>
        </td>
        <td style={{ ...cellStyle, color: '#5a5a4a', textAlign: 'right' }}>{filled} / {tt.total_spots}</td>
        <td style={{ ...cellStyle, color: '#5a5a4a', textAlign: 'right' }}>{tt.holes}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={beginEdit}>Edit</button>
        </td>
      </tr>
    );
  }

  // Edit mode — span across, two-column form
  return (
    <tr style={{ borderBottom: '0.5px solid #f0ede4', background: '#fafaf6' }}>
      <td colSpan={6} style={{ padding: '10px 6px' }}>
        <div style={{ fontSize: 11, color: '#7a7a6a', marginBottom: 8 }}>
          Editing <strong style={{ color: '#0d2b1a' }}>{tt.course_name}</strong> — {tt.host_name} · {filled} / {tt.total_spots} filled · silent edit (no player notifications)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
          <label style={{ fontSize: 11, color: '#5a5a4a' }}>
            Course
            <select
              value={form.courseId}
              onChange={e => setForm(f => ({ ...f, courseId: e.target.value }))}
              style={{ ...inputStyle, width: '100%', marginTop: 2 }}
            >
              <option value="">(unlinked from catalog)</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 11, color: '#5a5a4a' }}>
            Date
            <input type="date" value={form.teeDate} onChange={e => setForm(f => ({ ...f, teeDate: e.target.value }))} style={{ ...inputStyle, width: '100%', marginTop: 2 }} />
          </label>
          <label style={{ fontSize: 11, color: '#5a5a4a' }}>
            Time
            <input type="time" value={form.teeTime} onChange={e => setForm(f => ({ ...f, teeTime: e.target.value }))} style={{ ...inputStyle, width: '100%', marginTop: 2 }} />
          </label>
          <label style={{ fontSize: 11, color: '#5a5a4a' }}>
            Total spots
            <select value={form.totalSpots} onChange={e => setForm(f => ({ ...f, totalSpots: e.target.value }))} style={{ ...inputStyle, width: '100%', marginTop: 2 }}>
              {[2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 11, color: '#5a5a4a' }}>
            Holes
            <select value={form.holes} onChange={e => setForm(f => ({ ...f, holes: e.target.value }))} style={{ ...inputStyle, width: '100%', marginTop: 2 }}>
              <option value={9}>9</option>
              <option value={18}>18</option>
            </select>
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginBottom: 8 }}>
          <label style={{ fontSize: 11, color: '#5a5a4a' }}>
            Course URL (this tee time only)
            <input value={form.courseUrl} onChange={e => setForm(f => ({ ...f, courseUrl: e.target.value }))} placeholder="https://…" style={{ ...inputStyle, width: '100%', marginTop: 2 }} />
          </label>
          <label style={{ fontSize: 11, color: '#5a5a4a' }}>
            Course phone (this tee time only)
            <input value={form.coursePhone} onChange={e => setForm(f => ({ ...f, coursePhone: e.target.value }))} placeholder="(509) 555-1234" style={{ ...inputStyle, width: '100%', marginTop: 2 }} />
          </label>
          <label style={{ fontSize: 11, color: '#5a5a4a' }}>
            Cancellation URL
            <input value={form.cancellationUrl} onChange={e => setForm(f => ({ ...f, cancellationUrl: e.target.value }))} placeholder="https://…" style={{ ...inputStyle, width: '100%', marginTop: 2 }} />
          </label>
        </div>
        <div style={{ textAlign: 'right' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(false)} disabled={saving} style={{ marginRight: 4 }}>Cancel</button>
          <button type="button" className="btn btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </td>
    </tr>
  );
};

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [failures, setFailures] = useState([]);
  const [cancelled, setCancelled] = useState([]);
  const [liveTeeTimes, setLiveTeeTimes] = useState([]);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [courses, setCourses] = useState([]);
  const [courseQuery, setCourseQuery] = useState('');
  const [selectedCourseIds, setSelectedCourseIds] = useState(new Set());
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [s, h, f, c, u, a, co, lt] = await Promise.all([
        api.getAdminStats(),
        api.getAdminHealth(),
        api.getParseFailures().catch(() => []),
        api.getCancelledTeeTimes().catch(() => []),
        api.getAdminUsers().catch(() => []),
        api.getAuditLog().catch(() => []),
        api.getAdminCourses().catch(() => []),
        api.getAdminTeeTimes().catch(() => []),
      ]);
      setStats(s);
      setHealth(h);
      setFailures(f || []);
      setCancelled(c || []);
      setUsers(u || []);
      setAuditLog(a || []);
      setCourses(co || []);
      setLiveTeeTimes(lt || []);
    } catch (err) {
      setError(err.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-fetch users when search query changes (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      api.getAdminUsers(userQuery).then(setUsers).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [userQuery]);

  // Re-fetch courses when search query changes (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      api.getAdminCourses(courseQuery).then(setCourses).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [courseQuery]);

  if (!user?.is_admin) {
    return (
      <div className="empty-state">
        <p style={{ fontSize: 28 }}>🚫</p>
        <p>You don't have access to this page.</p>
      </div>
    );
  }

  if (loading) return <div className="empty-state"><p>Loading admin dashboard…</p></div>;
  if (error)   return <div className="empty-state"><p style={{ color: '#a13' }}>{error}</p></div>;

  const t = stats?.totals || {};
  const envEntries = health?.env ? Object.entries(health.env) : [];
  const allEnvOk = envEntries.every(([_, ok]) => ok);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'1.1rem', flexWrap:'wrap', gap:'.75rem' }}>
        <div>
          <h2 style={{ fontSize: 22 }}>Admin dashboard</h2>
          <p style={{ fontSize: 12, color: '#7a7a6a', marginTop: 1 }}>Stats, user management, system health</p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={load}>Refresh</button>
      </div>

      {/* Top stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '0.7rem', marginBottom: '1.5rem' }}>
        <StatCard label="Total users"      value={t.total_users}        hint={`+${t.new_users_7d} in last 7d`} />
        <StatCard label="Active users 7d"  value={stats?.activeUsers7d} hint="posted, joined, or commented" />
        <StatCard label="Tee times"        value={t.total_tee_times}    hint={`${t.upcoming_tee_times} upcoming · ${t.past_tee_times} past`} />
        <StatCard label="New tee times 7d" value={t.new_tee_times_7d}   hint="includes email imports" />
        <StatCard label="Groups"           value={t.total_groups} />
        <StatCard label="Joins"            value={t.total_joins}        hint="across all tee times" />
        <StatCard label="Comments"         value={t.total_comments} />
      </div>

      {/* Tee times by week */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a', marginBottom: 8 }}>
          Tee times posted (last 8 weeks)
        </div>
        <BarChart
          data={stats?.teeTimesByWeek || []}
          labelKey="week"
          valueKey="count"
          formatLabel={(d) => fmtDate(d).split(',')[0]}
        />
      </div>

      {/* Two-column row: peak hours + popular courses */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a', marginBottom: 8 }}>
            Peak tee times (by hour)
          </div>
          <BarChart
            data={stats?.peakHours || []}
            labelKey="hour"
            valueKey="count"
            formatLabel={fmtHour}
          />
        </div>
        <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a', marginBottom: 8 }}>
            Most popular courses
          </div>
          {(stats?.popularCourses || []).length === 0
            ? <div style={{ fontSize: 12, color: '#9a9a8a' }}>No data yet.</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {stats.popularCourses.map((c, i) => (
                    <tr key={i} style={{ borderBottom: i < stats.popularCourses.length - 1 ? '0.5px solid #f0ede4' : 'none' }}>
                      <td style={{ padding: '5px 0', color: '#0d2b1a' }}>{c.course_name}</td>
                      <td style={{ padding: '5px 0', color: '#5a5a4a', textAlign: 'right' }}>{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>

      {/* Recent signups */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a', marginBottom: 8 }}>
          Recent signups (last 20)
        </div>
        {(stats?.recentSignups || []).length === 0
          ? <div style={{ fontSize: 12, color: '#9a9a8a' }}>No users yet.</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e8e4d8' }}>
                  <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 11, textTransform: 'uppercase' }}>Name</th>
                  <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 11, textTransform: 'uppercase' }}>Email</th>
                  <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 11, textTransform: 'uppercase' }}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentSignups.map(u => (
                  <tr key={u.id} style={{ borderBottom: '0.5px solid #f0ede4' }}>
                    <td style={{ padding: '6px 0', color: '#0d2b1a' }}>{u.name}</td>
                    <td style={{ padding: '6px 0', color: '#5a5a4a' }}>{u.email}</td>
                    <td style={{ padding: '6px 0', color: '#7a7a6a', textAlign: 'right' }}>{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {/* User management */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a' }}>
            User management
            <span style={{ marginLeft: 8, fontSize: 11, background: '#f4f1eb', color: '#5a5a4a', padding: '2px 8px', borderRadius: 10 }}>{users.length}</span>
          </div>
          <input
            type="text"
            placeholder="Search name or email…"
            value={userQuery}
            onChange={e => setUserQuery(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #d8d4c4', borderRadius: 6, minWidth: 200 }}
          />
        </div>
        {users.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9a9a8a' }}>No users match.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e8e4d8' }}>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Name</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Email</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Phone</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }} title="hosted · joined">Activity</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 10, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <UserRow key={u.id} u={u} currentUserId={user.id} onChanged={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 10, color: '#9a9a8a', marginTop: 6 }}>
          Capped at 100 results · use search to filter · h = hosted, j = joined
        </div>
      </div>

      {/* Course management */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a' }}>
            Course catalog
            <span style={{ marginLeft: 8, fontSize: 11, background: '#f4f1eb', color: '#5a5a4a', padding: '2px 8px', borderRadius: 10 }}>{courses.length}</span>
          </div>
          <input
            type="text"
            placeholder="Search course name…"
            value={courseQuery}
            onChange={e => setCourseQuery(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #d8d4c4', borderRadius: 6, minWidth: 200 }}
          />
        </div>

        {/* Merge bar — appears when 2+ courses are selected */}
        {selectedCourseIds.size >= 2 && (
          <div style={{ background: '#f4f1eb', borderRadius: 8, padding: '8px 10px', marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#0d2b1a' }}>
              {selectedCourseIds.size} selected
            </span>
            <span style={{ fontSize: 12, color: '#5a5a4a' }}>→ keep:</span>
            <select
              value={mergeTargetId}
              onChange={e => setMergeTargetId(e.target.value)}
              style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #d8d4c4', borderRadius: 4 }}
            >
              <option value="">Pick target…</option>
              {[...selectedCourseIds].map(id => {
                const co = courses.find(x => x.id === id);
                return co ? <option key={id} value={id}>{co.name}</option> : null;
              })}
            </select>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!mergeTargetId}
              onClick={async () => {
                const target = courses.find(x => x.id === mergeTargetId);
                const sources = [...selectedCourseIds].filter(id => id !== mergeTargetId);
                if (!target || sources.length === 0) return;
                const sourceNames = sources.map(id => courses.find(x => x.id === id)?.name).filter(Boolean);
                if (!window.confirm(
                  `Merge ${sources.length} course(s) into "${target.name}"?\n\nMerging:\n  • ${sourceNames.join('\n  • ')}\n\nAll tee times at the merged courses will be repointed to "${target.name}". Sources will be deleted. This cannot be undone.`
                )) return;
                try {
                  const result = await api.mergeCourses(target.id, sources);
                  alert(`Merged. ${result.teeTimesUpdated} tee time(s) updated.`);
                  setSelectedCourseIds(new Set());
                  setMergeTargetId('');
                  load();
                } catch (err) { alert(err.message); }
              }}
            >Merge</button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => { setSelectedCourseIds(new Set()); setMergeTargetId(''); }}
            >Clear</button>
          </div>
        )}

        {courses.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9a9a8a' }}>No courses match.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e8e4d8' }}>
                  <th style={{ padding: '5px 6px', width: 28 }}></th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Name</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>URL</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Phone</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 10, textTransform: 'uppercase' }}># Tee&nbsp;Times</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 10, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map(co => (
                  <CourseRow
                    key={co.id}
                    c={co}
                    onChanged={load}
                    isSelected={selectedCourseIds.has(co.id)}
                    onSelectChange={(id, checked) => {
                      const next = new Set(selectedCourseIds);
                      if (checked) next.add(id); else next.delete(id);
                      setSelectedCourseIds(next);
                      // If target was deselected, clear it
                      if (!checked && mergeTargetId === id) setMergeTargetId('');
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 10, color: '#9a9a8a', marginTop: 6 }}>
          Capped at 200 · Edits propagate to all tee times referencing the course · Select 2+ rows to merge · Delete only available when 0 tee times reference the course
        </div>
      </div>

      {/* Parse failures */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a' }}>
            Email parse failures
            {failures.length > 0 && <span style={{ marginLeft: 8, fontSize: 11, background: '#fff0e8', color: '#a13', padding: '2px 8px', borderRadius: 10 }}>{failures.length}</span>}
          </div>
          <span style={{ fontSize: 11, color: '#9a9a8a' }}>auto-deleted after 30 days</span>
        </div>
        {failures.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9a9a8a' }}>No recent failures. 🎉</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {failures.map(f => (
              <details key={f.id} style={{ border: '1px solid #f0ede4', borderRadius: 8, padding: '0.5rem 0.7rem' }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#0d2b1a', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <strong style={{ color: '#5a5a4a' }}>{f.sender || '(unknown)'}</strong>
                    {f.subject ? ` — ${f.subject}` : ''}
                  </span>
                  <span style={{ fontSize: 10, color: '#9a9a8a', whiteSpace: 'nowrap' }}>{fmtDate(f.created_at)}</span>
                </summary>
                <div style={{ marginTop: 8, fontSize: 12, color: '#4a4a3a', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {f.reason && (
                    <div>
                      <div style={{ fontSize: 10, color: '#9a9a8a', textTransform: 'uppercase', letterSpacing: '.06em' }}>Reason</div>
                      <div style={{ color: '#a13' }}>{f.reason}</div>
                    </div>
                  )}
                  {f.body_excerpt && (
                    <div>
                      <div style={{ fontSize: 10, color: '#9a9a8a', textTransform: 'uppercase', letterSpacing: '.06em' }}>Email excerpt (500 chars)</div>
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f4f1eb', padding: 8, borderRadius: 6, fontSize: 11, fontFamily: 'monospace', margin: 0 }}>{f.body_excerpt}</pre>
                    </div>
                  )}
                  {f.claude_response && (
                    <div>
                      <div style={{ fontSize: 10, color: '#9a9a8a', textTransform: 'uppercase', letterSpacing: '.06em' }}>Claude's response</div>
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f4f1eb', padding: 8, borderRadius: 6, fontSize: 11, fontFamily: 'monospace', margin: 0 }}>{f.claude_response}</pre>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-outline btn-xs"
                      onClick={async () => {
                        if (!window.confirm('Delete this failure record?')) return;
                        try { await api.deleteParseFailure(f.id); load(); } catch (e) { alert(e.message); }
                      }}
                    >Delete</button>
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>

      {/* Live tee times — admin edit */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a' }}>
            Live tee times
            {liveTeeTimes.length > 0 && <span style={{ marginLeft: 8, fontSize: 11, background: '#f4f1eb', color: '#5a5a4a', padding: '2px 8px', borderRadius: 10 }}>{liveTeeTimes.length}</span>}
          </div>
          <span style={{ fontSize: 11, color: '#9a9a8a' }}>upcoming, next 100 — silent edit, no notifications</span>
        </div>
        {liveTeeTimes.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9a9a8a' }}>No upcoming tee times.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e8e4d8' }}>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left',  fontSize: 10, textTransform: 'uppercase' }}>Course</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left',  fontSize: 10, textTransform: 'uppercase' }}>Tee date/time</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'left',  fontSize: 10, textTransform: 'uppercase' }}>Host</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 10, textTransform: 'uppercase' }}>Filled</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 10, textTransform: 'uppercase' }}>Holes</th>
                  <th style={{ padding: '5px 6px', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 10, textTransform: 'uppercase' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {liveTeeTimes.map(tt => (
                  <LiveTeeTimeRow key={tt.id} tt={tt} courses={courses} onChanged={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cancelled tee times */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a' }}>
            Cancelled tee times
            {cancelled.length > 0 && <span style={{ marginLeft: 8, fontSize: 11, background: '#f4f1eb', color: '#5a5a4a', padding: '2px 8px', borderRadius: 10 }}>{cancelled.length}</span>}
          </div>
          <span style={{ fontSize: 11, color: '#9a9a8a' }}>last 50 — soft-deleted</span>
        </div>
        {cancelled.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9a9a8a' }}>No cancelled tee times.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e8e4d8' }}>
                <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Course</th>
                <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Tee date/time</th>
                <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Host</th>
                <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Cancelled by</th>
                <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 10, textTransform: 'uppercase' }}>Cancelled at</th>
                <th style={{ padding: '5px 0', color: '#9a9a8a', fontWeight: 500, textAlign: 'right', fontSize: 10, textTransform: 'uppercase' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {cancelled.map(c => (
                <tr key={c.id} style={{ borderBottom: '0.5px solid #f0ede4' }}>
                  <td style={{ padding: '6px 0', color: '#0d2b1a' }}>{c.course_name}</td>
                  <td style={{ padding: '6px 0', color: '#5a5a4a' }}>
                    {c.tee_date ? fmtDate(c.tee_date) : ''} {c.tee_time?.substring(0, 5) || ''}
                  </td>
                  <td style={{ padding: '6px 0', color: '#5a5a4a' }}>{c.host_name}<br/><span style={{ fontSize: 10, color: '#9a9a8a' }}>{c.host_email}</span></td>
                  <td style={{ padding: '6px 0', color: '#5a5a4a' }}>{c.deleted_by_name || '—'}</td>
                  <td style={{ padding: '6px 0', color: '#7a7a6a', textAlign: 'right' }}>{fmtDate(c.deleted_at)}</td>
                  <td style={{ padding: '6px 0', textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={async () => {
                        if (!window.confirm(`Restore "${c.course_name}"?`)) return;
                        try {
                          await api.restoreTeeTime(c.id);
                          load();
                        } catch (err) { alert(err.message); }
                      }}
                    >Restore</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Admin audit log */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a' }}>
            Admin audit log
            {auditLog.length > 0 && <span style={{ marginLeft: 8, fontSize: 11, background: '#f4f1eb', color: '#5a5a4a', padding: '2px 8px', borderRadius: 10 }}>{auditLog.length}</span>}
          </div>
          <span style={{ fontSize: 11, color: '#9a9a8a' }}>last 50 actions</span>
        </div>
        {auditLog.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9a9a8a' }}>No admin actions recorded yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {auditLog.map(a => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 11, padding: '5px 0', borderBottom: '0.5px solid #f0ede4', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontFamily: 'monospace', background: '#f4f1eb', padding: '1px 6px', borderRadius: 4, color: '#5a5a4a' }}>{a.action}</span>
                  <span style={{ marginLeft: 8, color: '#5a5a4a' }}>by {a.admin_name}</span>
                  {a.details && (
                    <div style={{ marginTop: 3, fontSize: 10, color: '#7a7a6a', fontFamily: 'monospace' }}>
                      {Object.entries(a.details).map(([k, v]) => (
                        <span key={k} style={{ marginRight: 10 }}>
                          {k}: {typeof v === 'object' && v !== null ? `${v.from ?? ''} → ${v.to ?? ''}` : String(v)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, color: '#9a9a8a', whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System health */}
      <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1rem' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#0d2b1a', marginBottom: 8 }}>
          System health
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#9a9a8a' }}>Backend uptime</div>
            <div style={{ fontSize: 14, color: '#0d2b1a' }}>{fmtUptime(health?.uptimeSeconds || 0)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#9a9a8a' }}>Node version</div>
            <div style={{ fontSize: 14, color: '#0d2b1a' }}>{health?.nodeVersion || '?'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#9a9a8a' }}>Database</div>
            <div style={{ fontSize: 14, color: health?.db?.ok ? '#1a5c32' : '#a13' }}>
              {health?.db?.ok ? `✓ Connected (${health.db.latencyMs}ms)` : '✗ Down'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#9a9a8a' }}>Env vars</div>
            <div style={{ fontSize: 14, color: allEnvOk ? '#1a5c32' : '#a13' }}>
              {allEnvOk ? '✓ All present' : `✗ ${envEntries.filter(([_, ok]) => !ok).length} missing`}
            </div>
          </div>
        </div>
        <details>
          <summary style={{ fontSize: 12, color: '#5a5a4a', cursor: 'pointer' }}>Env var details</summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4, marginTop: 8 }}>
            {envEntries.map(([k, ok]) => (
              <div key={k} style={{ fontSize: 11, color: ok ? '#1a5c32' : '#a13' }}>
                {ok ? '✓' : '✗'} {k}
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}
